/** Who made a change: a crewmate with its own name, or the owner's own. */
export type AgentPointerIdentity =
  | { readonly kind: 'named'; readonly name: string }
  | { readonly kind: 'owner' };

/** A place an agent step changed, as its live update names it. */
export interface AgentOriginTarget {
  readonly kind: string;
  readonly docId?: string;
  readonly blockId?: string;
  readonly elementId?: string;
  readonly workflowId?: string;
  readonly nodeId?: string;
  readonly rowId?: string;
  readonly columnId?: string;
  readonly viewControl?: string;
}

/** A control in the header of a database view: filter, sort or group. */
export type AgentViewControl = 'filter' | 'sort' | 'group';
const VIEW_CONTROLS = new Set<string>(['filter', 'sort', 'group']);

/**
 * Transaction origin of a live server update an agent step made. The client
 * applies such an update with this origin, so history and pointers can tell a
 * crewmate's change from a person's. It lives only for the transaction; local
 * storage keeps the bytes alone. The store defines what it reads; the update
 * transport builds the object.
 */
export interface AgentOrigin {
  readonly kind: 'agent';
  readonly runId: string;
  readonly stepId: string;
  readonly ownerUserId: string;
  readonly agent: AgentPointerIdentity;
  readonly targets: readonly AgentOriginTarget[];
}

export function isAgentOrigin(origin: unknown): origin is AgentOrigin {
  return (
    typeof origin === 'object' &&
    origin !== null &&
    (origin as { kind?: unknown }).kind === 'agent'
  );
}

/** An object of the document an agent step changed. */
export interface AgentPointerTarget {
  kind: 'canvas_element' | 'block';
  id: string;
  rowId?: string;
  columnId?: string;
  /** The header control of the database view a change of the view set. */
  viewControl?: AgentViewControl;
}

/**
 * A crewmate working in this document, shown where its run changed something
 * last. It stays until the run ends; a further change only moves it.
 */
export interface AgentPointer {
  readonly stepId: string;
  readonly ownerUserId: string;
  readonly runId: string;
  readonly agent: AgentPointerIdentity;
  /** What its latest change here touched; the pointer stands at the last one. */
  readonly targets: readonly AgentPointerTarget[];
}

/** One pointer per owner and run, wherever its changes come from. */
export function agentPointerKey(pointer: {
  ownerUserId: string;
  runId: string;
}): string {
  return `${pointer.ownerUserId}:${pointer.runId}`;
}

/**
 * The pointer an agent update moves in the document `docId`, or null when the
 * update names nothing of it (a new document, another document's objects).
 */
export function agentPointerOf(
  origin: AgentOrigin,
  docId: string
): AgentPointer | null {
  const targets = origin.targets.flatMap((target): AgentPointerTarget[] => {
    if (target.docId !== docId) return [];
    if (target.kind === 'node' && target.nodeId)
      return [{ kind: 'canvas_element', id: target.nodeId }];
    if (target.kind === 'canvas_element' && target.elementId)
      return [{ kind: 'canvas_element', id: target.elementId }];
    if (target.kind === 'block' && target.blockId)
      return [
        {
          kind: 'block',
          id: target.blockId,
          ...(target.rowId ? { rowId: target.rowId } : {}),
          ...(target.columnId ? { columnId: target.columnId } : {}),
          ...(target.viewControl && VIEW_CONTROLS.has(target.viewControl)
            ? { viewControl: target.viewControl as AgentViewControl }
            : {}),
        },
      ];
    return [];
  });
  if (targets.length === 0) return null;
  return {
    ownerUserId: origin.ownerUserId,
    runId: origin.runId,
    stepId: origin.stepId,
    agent: origin.agent,
    targets,
  };
}
